import { Search, FileText, Globe, Link2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Chip, Input } from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'

export type DashboardFilter = 'all' | 'drafts' | 'published' | 'link'

const FILTERS = [
  { id: 'all', label: 'filterAll', icon: null },
  { id: 'drafts', label: 'filterDrafts', icon: FileText },
  { id: 'published', label: 'filterPublished', icon: Globe },
  { id: 'link', label: 'filterLink', icon: Link2 },
] as const satisfies readonly {
  id: DashboardFilter
  label: keyof (typeof messages)['ru']
  icon: LucideIcon | null
}[]

interface Props {
  filter: DashboardFilter
  /** `null` — списки ещё грузятся: чисел не показываем, чтобы не врать. */
  counts: Record<DashboardFilter, number> | null
  search: string
  hasCourses: boolean
  onFilterChange: (f: DashboardFilter) => void
  onSearchChange: (v: string) => void
}

const Chips = ({
  filter,
  counts,
  onFilterChange,
}: Pick<Props, 'filter' | 'counts' | 'onFilterChange'>) => {
  const m = useMessages(messages)
  return (
    <>
      {FILTERS.map((f) => (
        <Chip
          key={f.id}
          active={filter === f.id}
          onClick={() => onFilterChange(f.id)}
          icon={f.icon ?? undefined}
          count={counts?.[f.id]}
        >
          {m[f.label]}
        </Chip>
      ))}
    </>
  )
}

/**
 * Шапка дашборда без заголовка и описания: страница начинается с дела. Поиск
 * на всю ширину, под ним фильтры; «Создать курс» живёт первой карточкой сетки
 * (`CreateCourseCard`), а не кнопкой в шапке.
 */
export const DashboardHeader = ({
  filter,
  counts,
  search,
  hasCourses,
  onFilterChange,
  onSearchChange,
}: Props) => {
  const m = useMessages(messages)
  return hasCourses ? (
    <div className="relative z-20 mb-6 space-y-4 md:mb-8">
      <div className="relative">
        <Search className="absolute left-5 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={m.searchPlaceholder}
          className="h-14 rounded-2xl border-0 bg-foreground/5 pl-14 text-base"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Chips filter={filter} counts={counts} onFilterChange={onFilterChange} />
      </div>
    </div>
  ) : null
}
